// 1. Imports React
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// 2. Imports externes
import {
  ArrowLeft,
  Building,
  Calendar,
  ChevronDown,
  CircleCheckBig,
  CircleSlash2,
  Clock,
  Edit,
  ToggleLeft,
  ToggleRight,
  TrendingUp,
} from "lucide-react";

// 3. Imports composants UI
import { EmployeeFormDialog } from "@/components/forms/EmployeeFormDialog";
import UserSessions, {
  type UserSessionsLabels,
  type UserSessionsStats,
} from "@/components/UserSessions";
import { UserInfos } from "@/components/UserInfos";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { showErrorToast, showSuccessToast } from "@/components/ui/toast";

// 4. Imports hooks personnalisés
import { useUpdateUser, useUserById } from "@/hooks/useUser";

// 5. Imports utils
import { getInitials } from "@/lib/utils";

// 6. Composant
export const EmployeeDetails = () => {
  // ========== A. Hooks React ==========
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  // Infos repliées par défaut → sessions visibles immédiatement
  const [infosOpen, setInfosOpen] = useState(false);
  const [sessionsStats, setSessionsStats] = useState<UserSessionsStats>({
    totalHours: "0h 0m",
    totalSessions: 0,
    averageDaily: "0h 0m",
    sessionsThisMonth: 0,
  });
  const [sessionsLabels, setSessionsLabels] = useState<UserSessionsLabels>({
    totalHours: "Total heures (ce mois)",
    sessions: "Sessions (ce mois)",
    average: "Moyenne / jour",
    subtitle: "Pour ce mois",
  });

  // ========== B. Custom Hooks ==========
  const {
    data: userData,
    isLoading,
    error: queryError,
  } = useUserById(id || "");
  const user = userData?.user;
  const { mutate: toggleUserStatus } = useUpdateUser();

  // ========== C. Handlers ==========
  // Fermer le modal après modification réussie
  const handleEditSuccess = () => {
    setIsEditDialogOpen(false);
  };

  // Ouvrir le modal de modification
  const handleEdit = () => {
    setIsEditDialogOpen(true);
  };

  // Basculer le statut actif/inactif de l'utilisateur
  const handleToggleUserStatus = () => {
    if (!user || !user.id) return;
    toggleUserStatus(
      {
        id: user.id,
        data: {
          isActive: !user.isActive,
        },
      },
      {
        onSuccess: () => {
          showSuccessToast(
            user.isActive
              ? "Utilisateur désactivé avec succès"
              : "Utilisateur activé avec succès"
          );
        },
        onError: (error: Error) => {
          showErrorToast(
            error.message || "Erreur lors de la modification du statut"
          );
        },
      }
    );
  };

  // Callback pour recevoir les stats et labels du composant UserSessions
  const handleStatsChange = useCallback(
    (stats: UserSessionsStats, labels: UserSessionsLabels) => {
      setSessionsStats(stats);
      setSessionsLabels(labels);
    },
    []
  );

  // ========== D. Gestion des erreurs et états de chargement ==========
  // Vérifier que l'ID existe
  if (!id) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Retour</span>
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold">
            Erreur
          </h1>
        </div>
        <Card className="p-4 sm:p-6">
          <div className="text-center">
            <p className="text-sm sm:text-base text-red-600 mb-2">
              ID utilisateur manquant
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Afficher l'erreur si la requête a échoué
  if (queryError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Retour</span>
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold">
            Erreur
          </h1>
        </div>
        <Card className="p-4 sm:p-6">
          <div className="text-center">
            <p className="text-sm sm:text-base text-red-600 mb-2">Erreur</p>
            <p className="text-sm sm:text-base text-gray-600">
              {queryError?.message}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Afficher le skeleton pendant le chargement
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="outline"
              size="sm"
              disabled
              onClick={() => navigate(-1)}
              className="gap-2 shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Retour</span>
            </Button>
            <div>
              <Skeleton className="h-8 w-48 sm:w-64" />
              <Skeleton className="h-4 w-32 mt-2" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="p-4 sm:p-6">
              <div className="space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-8 w-1/2" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Afficher un message si l'utilisateur n'est pas trouvé
  if (!user) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Retour</span>
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold">
            Utilisateur non trouvé
          </h1>
        </div>
      </div>
    );
  }

  // ========== E. JSX ==========
  return (
    <div className="space-y-6">
      {/* En-tête avec avatar et actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Avatar className="h-10 w-10 sm:h-12 sm:w-12 bg-white shrink-0">
            <AvatarFallback className="text-sm sm:text-base font-semibold">
              {getInitials(user?.firstName, user?.lastName)}
            </AvatarFallback>
          </Avatar>
          <div className=" lg:ml-0 min-w-0 flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary truncate">
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.firstName || user?.lastName || "Utilisateur"}
              </h1>
              <Badge
                variant={user?.isActive ? "default" : "secondary"}
                className={
                  user?.isActive
                    ? "bg-success hover:bg-success w-fit"
                    : "bg-error hover:bg-error text-error-foreground w-fit"
                }
              >
                {user?.isActive ? (
                  <CircleCheckBig className="h-3 w-3" />
                ) : (
                  <CircleSlash2 className="h-3 w-3" />
                )}{" "}
                {user?.isActive ? "Actif" : "Inactif"}
              </Badge>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Créé le{" "}
              {new Date(user?.createdAt ?? "").toLocaleDateString("fr-FR")}
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            className="gap-2 w-full sm:w-auto"
            onClick={handleToggleUserStatus}
            size="sm"
          >
            {user?.isActive ? (
              <>
                <ToggleLeft className="h-4 w-4" />
                Désactiver
              </>
            ) : (
              <>
                <ToggleRight className="h-4 w-4" />
                Activer
              </>
            )}
          </Button>
          <Button
            className="gap-2 w-full sm:w-auto"
            onClick={handleEdit}
            size="sm"
          >
            <Edit className="h-4 w-4" />
            Modifier
          </Button>
        </div>
      </div>

      {/* Cartes de statistiques */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {sessionsLabels.totalHours}
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{sessionsStats.totalHours}</div>
            <p className="text-xs text-muted-foreground">
              {sessionsLabels.subtitle}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {sessionsLabels.sessions}
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {sessionsStats.totalSessions}
            </div>
            <p className="text-xs text-muted-foreground">
              {`${sessionsStats.totalSessions} au total`}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {sessionsLabels.average}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {sessionsStats.averageDaily}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Entreprise</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{user?.company?.name}</div>
            <p className="text-xs text-muted-foreground">
              {user?.role === "ADMIN" ? "Administrateur" : "Employé"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Informations personnelles — repliées par défaut */}
      <Collapsible open={infosOpen} onOpenChange={setInfosOpen}>
        <Card className="overflow-hidden" >
          {/* En-tête cliquable pour déplier/replier */}
          <CollapsibleTrigger asChild className="cursor-pointer">
            <button
              type="button"
              className="flex w-full items-center justify-between p-4 sm:p-6 text-left hover:bg-muted/30 transition-colors"
            >
              <span className="font-semibold text-base">
                Informations personnelles
              </span>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                  infosOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </CollapsibleTrigger>

          {/* Contenu déplié */}
          <CollapsibleContent>
            <div className="px-4 pb-4 sm:px-6 sm:pb-6 border-t">
              <div className="pt-4 sm:pt-6">
                <UserInfos user={user} />
              </div>
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Sessions — toujours visible, plus besoin de chercher */}
      <Card className="p-4 sm:p-6">
        <UserSessions
          userId={id || ""}
          userName={user ? `${user.firstName} ${user.lastName}` : undefined}
          firstName={user?.firstName}
          lastName={user?.lastName}
          onStatsChange={handleStatsChange}
        />
      </Card>

      {/* Dialog de modification */}
      <EmployeeFormDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        employee={user}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default EmployeeDetails;
